import { useState } from "react";
import { AccountSurveyActionResponse } from "~/entities/actions/types";
import {
  BUY_FREQUENCY_MONTHLY_ANSWER_ID,
  BUY_FREQUENCY_QUESTION_ID,
  BUY_FREQUENCY_SEMI_ANNUALLY_ANSWER_ID,
  BUY_FREQUENCY_WEEKLY_ANSWER_ID,
  LICENSE_IN_PROCESS_ANSWER_ID,
  LICENSE_NO_ANSWER_ID,
  LICENSE_QUESTION_ID,
  LICENSE_YES_ANSWER_ID,
  PERMIT_NO_ANSWER_ID,
  PERMIT_NOT_SURE_ANSWER_ID,
  PERMIT_QUESTION_ID,
  PERMIT_YES_ANSWER_ID,
  WORK_FREQUENCY_FULL_TIME_ANSWER_ID,
  WORK_FREQUENCY_OCCASIONALLY_ANSWER_ID,
  WORK_FREQUENCY_PART_TIME_ANSWER_ID,
  WORK_FREQUENCY_QUESTION_ID,
} from "~/entities/survey/constants";
import useCreateAccountWizard from "~/hooks/useCreateAccountWizard";
import ErrorMessage from "./ErrorMessage";
import RadioField from "./RadioField";
import SubmitButton from "./SubmitButton";
import SurveyQuestion from "./SurveyQuestion";

const WorkFrequencyQuestion: React.FC = () => (
  <SurveyQuestion
    ordinal={1}
    question="How often do you work with blank apparel?"
  >
    <RadioField
      id="full_time"
      name={WORK_FREQUENCY_QUESTION_ID}
      value={WORK_FREQUENCY_FULL_TIME_ANSWER_ID}
    >
      Full-time
    </RadioField>
    <RadioField
      id="part_time"
      name={WORK_FREQUENCY_QUESTION_ID}
      value={WORK_FREQUENCY_PART_TIME_ANSWER_ID}
    >
      Part-time
    </RadioField>
    <RadioField
      id="occasionally"
      name={WORK_FREQUENCY_QUESTION_ID}
      value={WORK_FREQUENCY_OCCASIONALLY_ANSWER_ID}
    >
      Occasionally or as a hobby
    </RadioField>
  </SurveyQuestion>
);

const BuyFrequencyQuestion: React.FC = () => (
  <SurveyQuestion
    ordinal={2}
    question="How often do you buy apparel?"
  >
    <RadioField
      id="weekly"
      name={BUY_FREQUENCY_QUESTION_ID}
      value={BUY_FREQUENCY_WEEKLY_ANSWER_ID}
    >
      Weekly
    </RadioField>
    <RadioField
      id="monthly"
      name={BUY_FREQUENCY_QUESTION_ID}
      value={BUY_FREQUENCY_MONTHLY_ANSWER_ID}
    >
      Monthly
    </RadioField>
    <RadioField
      id="semi_annually"
      name={BUY_FREQUENCY_QUESTION_ID}
      value={BUY_FREQUENCY_SEMI_ANNUALLY_ANSWER_ID}
    >
      A couple times a year
    </RadioField>
  </SurveyQuestion>
);

const LicenseQuestion: React.FC<{
  checkedValue?: string;
  onChange: (value: string) => void;
}> = ({ checkedValue, onChange }) => (
  <SurveyQuestion
    ordinal={3}
    question="Do you have a business license?"
  >
    <RadioField
      id="license_yes"
      name={LICENSE_QUESTION_ID}
      value={LICENSE_YES_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Yes
    </RadioField>
    <RadioField
      id="license_no"
      name={LICENSE_QUESTION_ID}
      value={LICENSE_NO_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      No
    </RadioField>
    <RadioField
      id="license_in_process"
      name={LICENSE_QUESTION_ID}
      value={LICENSE_IN_PROCESS_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      In the process of getting one
    </RadioField>
  </SurveyQuestion>
);

const PermitQuestion: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  if (!isActive) return null;
  return (
    <SurveyQuestion
      ordinal={4}
      question="Do you have a reseller's permit?"
    >
      <RadioField
        id="permit_yes"
        name={PERMIT_QUESTION_ID}
        value={PERMIT_YES_ANSWER_ID}
      >
        Yes
      </RadioField>
      <RadioField
        id="permit_no"
        name={PERMIT_QUESTION_ID}
        value={PERMIT_NO_ANSWER_ID}
      >
        No
      </RadioField>
      <RadioField
        id="permit_not_sure"
        name={PERMIT_QUESTION_ID}
        value={PERMIT_NOT_SURE_ANSWER_ID}
      >
        Not sure
      </RadioField>
    </SurveyQuestion>
  );
};

type Props = {
  onSuccess: () => void;
  isActive: boolean;
};

const BusinessSurvey: React.FC<Props> = ({ onSuccess, isActive }) => {
  const { errors, fetcher } =
    useCreateAccountWizard<AccountSurveyActionResponse>(onSuccess);
  const [license, setLicense] = useState<string | undefined>(undefined);

  if (!isActive) return null;

  return (
    <fetcher.Form
      method="post"
      action="/resources/create-account-wizard"
      className="flex flex-col gap-5"
      data-testid="business-survey-form"
    >
      <input
        type="hidden"
        name="intent"
        value="business_survey"
      />
      <h2 className="text-[20px] text-blue900 font-sans mt-0 uppercase font-bold">
        Let&apos;s set up wholesale pricing.
      </h2>

      <ErrorMessage error={errors["form"]} />

      <WorkFrequencyQuestion />
      <BuyFrequencyQuestion />
      <LicenseQuestion
        checkedValue={license}
        onChange={(val) => setLicense(val)}
      />
      <PermitQuestion isActive={license === LICENSE_YES_ANSWER_ID} />

      <SubmitButton
        disabled={fetcher.state === "submitting"}
        label="Almost done"
      />
    </fetcher.Form>
  );
};

export default BusinessSurvey;
